import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { FiHeart, FiShoppingBag, FiX } from "react-icons/fi";
import { fetchWishlist, removeFromWishlist, clearWishlist } from "../../feature/wishlist";
import { addToCart } from "../../feature/cartSlice";

const formatPrice = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

function CardSkeleton() {
  return (
    <div className="animate-pulse overflow-hidden rounded-xl border border-[#e3e9dd] bg-white">
      <div className="aspect-square bg-gray-200" />
      <div className="space-y-2 p-4">
        <div className="h-3 w-16 rounded bg-gray-200" />
        <div className="h-4 w-3/4 rounded bg-gray-200" />
        <div className="h-4 w-1/3 rounded bg-gray-200" />
      </div>
    </div>
  );
}

function Wishlist() {
  const items = useSelector((state) => state.wishlist.items);
  const dispatch = useDispatch();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    // works whether fetchWishlist is a thunk (returns a promise) or not
    Promise.resolve(dispatch(fetchWishlist())).finally(() => {
      if (alive) setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, [dispatch]);

  const moveToCart = (item) => {
    dispatch(
      addToCart({
        id: item.id,
        name: item.name,
        price: item.price,
        image: item.image ?? item.img,
        qty: 1,
      })
    );
    dispatch(removeFromWishlist(item)); // move, not copy
  };

  const clearAll = () => {
    if (window.confirm("Remove all items from your wishlist?")) {
      dispatch(clearWishlist());
    }
  };

  /* ---------- loading (first visit only) ---------- */
  if (!loaded && items.length === 0) {
    return (
      <div className="min-h-screen bg-[#f6f8f2] pt-[60px]">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 sm:py-12">
          <div className="mb-6 h-8 w-48 animate-pulse rounded bg-gray-200" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4">
            {[0, 1, 2, 3].map((n) => (
              <CardSkeleton key={n} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ---------- empty ---------- */
  if (items.length === 0) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-[#f6f8f2] px-4 pt-[60px]">
        <div className="max-w-[360px] py-16 text-center">
          <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#eef5ea]">
            <FiHeart className="text-[34px] text-[#2f6b1f]" />
          </span>
          <h2 className="mt-5 text-[22px] font-semibold text-[#1f2d22] sm:text-[26px]">
            Your wishlist is empty
          </h2>
          <p className="mt-2 text-[14px] text-gray-500">
            Tap the heart on any product to save it here for later.
          </p>
          <Link
            to="/shop"
            className="mt-6 inline-block rounded-md bg-[#2f6b1f] px-8 py-3 text-[13px] font-semibold text-white transition-colors hover:bg-[#245416]"
          >
            Browse products
          </Link>
        </div>
      </div>
    );
  }

  /* ---------- list ---------- */
  return (
    <div className="min-h-screen bg-[#f6f8f2] pt-[60px]">
      <div className="mx-auto w-full max-w-[1240px] px-4 py-8 sm:py-12">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[24px] font-semibold text-[#1f2d22] sm:text-[30px]">
              My Wishlist
            </h1>
            <p className="mt-1 text-[13px] text-gray-500 sm:text-[14px]">
              {items.length} {items.length === 1 ? "item" : "items"} saved
            </p>
          </div>
          <button
            type="button"
            onClick={clearAll}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-[13px] font-medium text-gray-600 transition-colors hover:border-red-300 hover:text-red-600"
          >
            Clear wishlist
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="group relative flex flex-col overflow-hidden rounded-xl border border-[#e3e9dd] bg-white transition-shadow hover:shadow-md"
            >
              <Link to={`/product/${item.id}`} className="block">
                <div className="aspect-square w-full overflow-hidden bg-[#f4f7ef]">
                  <img
                    src={item.image}
                    alt={item.name}
                    onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-3 sm:p-4">
                  {item.category && (
                    <p className="mb-1 text-[12px] text-gray-500">{item.category}</p>
                  )}
                  <h3 className="line-clamp-2 text-[14px] leading-snug text-[#1f2d22] sm:text-[15px]">
                    {item.name}
                  </h3>
                  <p className="mt-2 text-[15px] font-semibold text-[#1f2d22]">
                    {formatPrice(item.price)}
                  </p>
                </div>
              </Link>

              <button
                type="button"
                onClick={() => dispatch(removeFromWishlist(item))}
                aria-label={`Remove ${item.name} from wishlist`}
                className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-gray-700 shadow transition-colors hover:bg-red-600 hover:text-white"
              >
                <FiX className="text-[16px]" />
              </button>

              <div className="mt-auto p-3 pt-0 sm:p-4 sm:pt-0">
                <button
                  type="button"
                  onClick={() => moveToCart(item)}
                  className="flex h-[40px] w-full items-center justify-center gap-2 rounded-md bg-[#2f6b1f] text-[13px] font-semibold text-white transition-colors hover:bg-[#245416]"
                >
                  <FiShoppingBag /> Move to cart
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Wishlist;
